/**
 * A deploy replaces Vite's content-hashed route chunks. An already-open tab
 * can still be running the previous app shell, whose next lazy route import
 * then points at a file that no longer exists. Reload once into the current
 * shell; if that cannot solve the problem, leave the error boundary visible
 * rather than repeatedly refreshing the page.
 */
const RELOAD_KEY_PREFIX = "dreambook:stale-chunk-reload:";

function errorText(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`.toLowerCase();
  return String(error).toLowerCase();
}

export function isStaleChunkError(error: unknown): boolean {
  const text = errorText(error);
  return (
    text.includes("failed to fetch dynamically imported module") ||
    text.includes("error loading dynamically imported module") ||
    text.includes("importing a module script failed") ||
    text.includes("failed to load module script") ||
    text.includes("chunkloaderror") ||
    text.includes("loading chunk")
  );
}

function currentShellKey(): string {
  // The entry filename changes on every Vite deployment. Scope the retry to
  // that shell, so a later deploy still gets its own one-time recovery.
  const entry = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src;
  return `${RELOAD_KEY_PREFIX}${entry ?? "unknown-shell"}`;
}

/** Returns true only when it has scheduled the one safe refresh. */
export function reloadForStaleChunk(error: unknown): boolean {
  if (typeof window === "undefined" || !isStaleChunkError(error)) return false;

  try {
    const key = currentShellKey();
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, "1");
    window.location.reload();
    return true;
  } catch {
    // Storage can be unavailable in privacy-restricted contexts. In that case
    // the visible fallback is safer than risking a reload loop.
    return false;
  }
}

/**
 * Vite emits this event when its lazy-import preload wrapper cannot load a
 * deployed chunk. Catch it before React receives the rejected import.
 */
export function installStaleChunkRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadForStaleChunk(event.payload)) event.preventDefault();
  });
}
